// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the figure and methods dialog (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`,
 * §Next S4 and S5), opened from the Analyze panel on the plans with the
 * research tools.
 *
 * **Figure:** the globe as rendered, or the whole frame on the Equal Earth
 * projection (never Mercator), one or two columns wide at 300 dpi, with an
 * editable title, the colour bar in the data's units (or the change's
 * scale while a change is shown), the pins if wanted, and the citation as
 * the caption. PNG (with its 300 dpi and caption in the file) and SVG (text,
 * colour bar, coastlines and pins as vectors; the map stays a raster). The
 * layout, the PNG metadata and the SVG are `research/figure.ts`.
 *
 * **Methods:** the paragraph `research/methods.ts` writes from what the app
 * did, with its references, to copy or download.
 *
 * The figure's own text is English, like the citation it carries.
 */

import { t } from '../i18n'
import { formatNumber } from '../i18n/format'
import { escapeAttr, escapeHtml } from './domUtils'
import { citationText, formatFrameTime, type CitationInput } from '../services/research/citation'
import {
  EQUAL_EARTH_ASPECT,
  FIGURE_DPI,
  FIGURE_FONT,
  FIGURE_WIDTH_MM,
  changeTicks,
  equalEarthCoasts,
  equalEarthFrame,
  equalEarthProjector,
  equalEarthRaster,
  figureSvg,
  layoutFigure,
  mmToPx,
  onWhite,
  tickLabel,
  withPngMetadata,
  type FigureKey,
  type FigureLayout,
  type FigureLegendSpec,
  type FigureMark,
  type FigureSize,
  type Measure,
  type Rgb,
} from '../services/research/figure'
import { buildMethods, methodsText, type MethodsFigure, type MethodsInput } from '../services/research/methods'
import { trail, trailFile } from '../services/research/researchTrail'
import {
  CHANGE_GREY,
  CHANGE_GRID_WIDTH,
  PALETTE_STEPS,
  buildChangeLut,
  buildChangePalette,
  changeBarStops,
  computePeriodChangeGrid,
  paintChangeGrid,
  valueTable,
  type ChangeColours,
  type ChangeMode,
} from '../services/research/changeMap'
import type { Climatology } from '../services/research/climatology'
import { latLonToTexelUv } from '../services/datasetProbe'
import { DEFAULT_DISPLAY, buildDisplayLut, colorbarTicks, displayGradientStops, isDefaultDisplay, valueAtPosition, type ColorScaleDisplay } from '../services/colorScaleDisplay'
import { releaseOf, type ColorScale } from '../types/color-scale'
import type { DatasetOverlayOptions } from '../types'
import type { LumaSnapshot } from '../services/glLumaSampler'
import { ayniCsvFilename } from './pinsCsv'

export type FigureKind = 'globe' | 'equal-earth'
export type FigureTab = 'figure' | 'methods'

/** The frame on screen, for the Equal Earth map and the colour bar. */
export interface FigureFrame {
  readonly snapshot: LumaSnapshot
  readonly scale: ColorScale
  readonly options: DatasetOverlayOptions
  readonly display: ColorScaleDisplay
}

/** The change the globe shows, when it shows one. */
export type FigureChange =
  | {
      readonly kind: 'baseline'
      readonly mode: ChangeMode
      readonly limit: number
      /** AYNI: the − end of the viewer's own range; −limit when not given. */
      readonly min?: number
      /** AYNI: the change map's palette; RdBu when not given. */
      readonly colours?: ChangeColours
      readonly floor: number | null
      /** The held frame's codes; the same grid as the frame on screen. */
      readonly base: { readonly data: Uint8Array; readonly width: number; readonly height: number; readonly scale: ColorScale }
      readonly frameLabel: string | null
    }
  | {
      /** AYNI S2: a period's climatology as the baseline; the change of the frame on screen against its group. */
      readonly kind: 'period'
      readonly mode: ChangeMode
      /** ± limit: the fixed range (±3 for σ change). */
      readonly limit: number
      /** AYNI: the − end of the viewer's own range; −limit when not given. */
      readonly min?: number
      /** AYNI: the change map's palette; RdBu when not given. */
      readonly colours?: ChangeColours
      readonly floor: number | null
      readonly clim: Climatology
      /** The group (calendar month, or month and UTC hours) of the frame on screen. */
      readonly group: number
      /** "1991–2020". */
      readonly span: string
      /** The group in English ("July", "July, 12:00–18:00 UTC"); null for annual data or a window. */
      readonly groupLabel: string | null
    }
  | {
      readonly kind: 'compare'
      /** The difference as drawn on the globe: a global equirectangular picture, north up. */
      readonly canvas: HTMLCanvasElement
      readonly limit: number
      /** AYNI: the − end of the viewer's own range; −limit when not given. */
      readonly min?: number
      /** AYNI: the change map's palette; RdBu when not given. */
      readonly colours?: ChangeColours
      readonly otherTitle: string
      /** A − B, or (A − B) / |B| as a % of B. */
      readonly mode?: 'difference' | 'percent'
    }

export interface FigurePin {
  readonly lat: number
  readonly lon: number
  readonly label: string
  readonly color: string
}

/** The globe as rendered, cropped to the figure's map. */
export interface GlobeCapture {
  /** Exactly the size asked for. */
  readonly canvas: HTMLCanvasElement
  /** The render it was cut from, in device pixels (smaller than `canvas`: enlarged). */
  readonly source: { readonly width: number; readonly height: number }
  /** Where a place falls in the capture, 0..1 each way; null behind the globe or outside the crop. */
  project(lat: number, lon: number): { x: number; y: number } | null
}

export interface FigureSource {
  datasetTitle(): string | null
  units(): string | null
  citation(): CitationInput | null
  frame(): FigureFrame | null
  change(): FigureChange | null
  pins(): readonly FigurePin[]
  captureGlobe(width: number, height: number): Promise<GlobeCapture | null>
  /** The methods input now, with the figure exported in this dialog (null: none yet). */
  methods(figure: MethodsFigure | null): MethodsInput | null
  /** Stop playback, so the figure and the numbers describe one frame; true when it was playing. */
  pause?(): boolean
}

const DIALOG_ID = 'figure-dialog'
const NO_DATA: Rgb = [217, 217, 217]
const COASTS_URL = '/assets/ne_110m_land.geojson'
let coasts: Promise<unknown> | null = null

function loadCoasts(): Promise<unknown> {
  if (!coasts) {
    coasts = fetch(COASTS_URL).then((r) => (r.ok ? r.json() : null)).catch(() => null)
  }
  return coasts
}

let measureCtx: CanvasRenderingContext2D | null = null
/** Text widths in the figure's font, from a canvas, so the PNG and the SVG wrap alike. */
const measure: Measure = (text, px, bold) => {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d')
  // No canvas text (a test's DOM): Arial's average width.
  if (typeof measureCtx?.measureText !== 'function') return text.length * px * (bold ? 0.6 : 0.55)
  measureCtx!.font = `${bold ? 'bold ' : ''}${px}px ${FIGURE_FONT}`
  return measureCtx!.measureText(text).width
}

// ------------------------------------------------------------------ legend

/** The colour bar or class list under the map, in the figure's English. */
export function figureLegend(frame: FigureFrame | null, change: FigureChange | null, units: string | null, title: string, kind: FigureKind): FigureLegendSpec | null {
  const u = units ? ` (${units})` : ''
  const noData: FigureKey[] = kind === 'equal-earth' ? [{ rgb: NO_DATA, label: frame?.options.boundingBox ? 'No data, or outside the dataset' : 'No data' }] : [] // i18n-exempt: figure text, English like the citation
  if (change) {
    const bar = changeBarStops(change.colours)
    const stops = bar.map((c, i) => ({ pos: i / (bar.length - 1), rgb: [c[0], c[1], c[2]] as Rgb }))
    const grey: FigureKey = {
      rgb: [CHANGE_GREY[0], CHANGE_GREY[1], CHANGE_GREY[2]],
      label: change.kind === 'compare'
        ? change.mode === 'percent'
          ? 'Only one of the two has data, or B too close to zero for a percentage' // i18n-exempt: figure text
          : 'Only one of the two has data' // i18n-exempt: figure text, English like the citation
        : change.kind === 'period'
          ? change.mode === 'sigma'
            ? 'No σ change: the frame or the climatology without data, or no σ' // i18n-exempt: figure text
            : change.mode === 'percent'
              ? 'No meaningful change: the frame or the climatology without data, or a mean near zero' // i18n-exempt: figure text
              : 'No meaningful change: the frame or the climatology without data' // i18n-exempt: figure text
          : change.mode === 'percent'
            ? 'No meaningful change: one frame without data, or a baseline near zero' // i18n-exempt: figure text
            : 'No meaningful change: one frame without data', // i18n-exempt: figure text
    }
    const legendTitle = change.kind === 'compare'
      ? change.mode === 'percent'
        ? `(${title} − ${change.otherTitle}) as % of ${change.otherTitle}` // i18n-exempt: figure text
        : `${title} − ${change.otherTitle}${u}`
      : change.kind === 'period'
        ? periodLegendTitle(change, u)
        : change.mode === 'percent'
          ? `% change since ${change.frameLabel ?? 'the baseline'}` // i18n-exempt: figure text
          : `Change since ${change.frameLabel ?? 'the baseline'}${u}` // i18n-exempt: figure text
    const suffix = change.mode === 'percent' ? ' %' : change.kind !== 'compare' && change.mode === 'sigma' ? ' σ' : ''
    const ticks = changeTicks(change.limit, change.min).map((tk) => ({ ...tk, label: `${tk.label}${suffix}` }))
    const none: FigureKey[] = kind === 'equal-earth' ? [{ rgb: NO_DATA, label: change.kind === 'compare' ? 'Neither has data' : change.kind === 'period' ? 'Neither the frame nor the climatology has data' : 'Neither frame has data' }] : [] // i18n-exempt: figure text
    return { kind: 'bar', title: legendTitle, stops, ticks, keys: [grey, ...none] }
  }
  if (!frame) return null
  const release = releaseOf(frame.scale)
  if (release?.kind === 'luma8-classified' && release.classes.length) {
    const lut = buildDisplayLut(frame.scale, DEFAULT_DISPLAY)
    const items = release.classes.map((c) => {
      const o = Math.max(0, Math.min(255, Math.round(c.code))) * 4
      return { rgb: onWhite(lut[o], lut[o + 1], lut[o + 2], 255), label: c.label || String(c.value) }
    })
    return { kind: 'classes', title, items, keys: noData }
  }
  const stops = displayGradientStops(frame.scale, frame.display, 64).map((s) => ({ pos: s.position, rgb: onWhite(...s.rgba) }))
  const ticks = colorbarTicks(frame.scale, frame.display, 6).map((tk) => ({ pos: tk.position, label: tickLabel(tk.value) }))
  const hidden: FigureKey[] = kind === 'equal-earth' && (frame.display.threshold.min !== null || frame.display.threshold.max !== null)
    ? [{ rgb: [255, 255, 255], label: 'Hidden by the colour limits' }] // i18n-exempt: figure text
    : []
  return { kind: 'bar', title: `${title}${u}`, stops, ticks, keys: [...noData, ...hidden] }
}

/** A period change's legend title, in the figure's English: what, against which climatology and group, in what units. */
function periodLegendTitle(change: Extract<FigureChange, { kind: 'period' }>, units: string): string {
  const against = `the ${change.span} climatology${change.groupLabel ? ` (${change.groupLabel})` : ''}`
  if (change.mode === 'sigma') return `Standardised change from ${against}, σ` // i18n-exempt: figure text
  if (change.mode === 'percent') return `% change from ${against}` // i18n-exempt: figure text
  return `Change from ${against}${units}` // i18n-exempt: figure text
}

// ------------------------------------------------------------------ the map rasters

/** The whole frame on the Equal Earth projection, `width` × `height`, as the globe colours it. */
export function paintEqualEarth(frame: FigureFrame, change: FigureChange | null, width: number, height: number): HTMLCanvasElement {
  const { snapshot, scale, options } = frame
  const W = snapshot.width
  const H = snapshot.height
  const texel = (lat: number, lon: number): number => {
    const uv = latLonToTexelUv(lat, lon, options)
    if (!uv) return -1
    const x = Math.min(W - 1, Math.max(0, Math.floor(uv.u * W)))
    const y = Math.min(H - 1, Math.max(0, Math.floor(uv.v * H)))
    return y * W + x
  }
  const put = (out: Uint8ClampedArray, o: number, c: Rgb) => {
    out[o] = c[0]
    out[o + 1] = c[1]
    out[o + 2] = c[2]
    out[o + 3] = 255
  }
  let sample: (lat: number, lon: number, out: Uint8ClampedArray, o: number) => void
  if (change?.kind === 'baseline' && change.base.width === W && change.base.height === H) {
    // The table the globe's shader reads: one colour per (now, baseline) pair, by the same rules.
    const lut = buildChangeLut(scale, change.base.scale, change.mode, change.floor, { min: change.min ?? -change.limit, max: change.limit }, 255, change.colours)
    sample = (lat, lon, out, o) => {
      const k = texel(lat, lon)
      if (k < 0) return put(out, o, NO_DATA)
      const i = (change.base.data[k] * 256 + snapshot.data[k]) * 4
      if (lut[i + 3] === 0) return put(out, o, NO_DATA)
      put(out, o, [lut[i], lut[i + 1], lut[i + 2]])
    }
  } else if (change?.kind === 'period' && change.clim.width === W && change.clim.height === H) {
    // The paused picture's own computation (`computePeriodChangeGrid`, the GPU's palette): each texel against
    // its group's mean (and σ), read at the climatology's texel when it was thinned, on a global grid.
    const gw = W > CHANGE_GRID_WIDTH ? 2 * CHANGE_GRID_WIDTH : CHANGE_GRID_WIDTH
    const gh = gw / 2
    const grid = computePeriodChangeGrid({
      now: snapshot, nowScale: scale, clim: change.clim, group: change.group, options,
      mode: change.mode, step: null, width: gw, height: gh, limit: change.limit, range: { min: change.min ?? -change.limit, max: change.limit }, floor: change.floor,
    })
    const px = grid ? paintChangeGrid(grid, 255, buildChangePalette(PALETTE_STEPS, change.colours)) : null
    sample = (lat, lon, out, o) => {
      if (!px) return put(out, o, NO_DATA)
      const x = Math.min(gw - 1, Math.max(0, Math.floor(((lon + 180) / 360) * gw)))
      const y = Math.min(gh - 1, Math.max(0, Math.floor(((90 - lat) / 180) * gh)))
      const i = (y * gw + x) * 4
      if (px[i + 3] === 0) return put(out, o, NO_DATA)
      put(out, o, [px[i], px[i + 1], px[i + 2]])
    }
  } else if (change?.kind === 'compare') {
    const c = change.canvas
    const px = c.getContext('2d')?.getImageData(0, 0, c.width, c.height).data ?? null
    sample = (lat, lon, out, o) => {
      if (!px) return put(out, o, NO_DATA)
      const x = Math.min(c.width - 1, Math.max(0, Math.floor(((lon + 180) / 360) * c.width)))
      const y = Math.min(c.height - 1, Math.max(0, Math.floor(((90 - lat) / 180) * c.height)))
      const i = (y * c.width + x) * 4
      if (px[i + 3] === 0) return put(out, o, NO_DATA)
      put(out, o, [px[i], px[i + 1], px[i + 2]])
    }
  } else {
    const lut = buildDisplayLut(scale, frame.display)
    const values = valueTable(scale)
    sample = (lat, lon, out, o) => {
      const k = texel(lat, lon)
      if (k < 0) return put(out, o, NO_DATA)
      const code = snapshot.data[k]
      if (!Number.isFinite(values[code])) return put(out, o, NO_DATA)
      const i = code * 4
      put(out, o, onWhite(lut[i], lut[i + 1], lut[i + 2], lut[i + 3]))
    }
  }
  const pixels = equalEarthRaster(width, height, sample)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.getContext('2d')?.putImageData(new ImageData(pixels as Uint8ClampedArray<ArrayBuffer>, width, height), 0, 0)
  return canvas
}

// ------------------------------------------------------------------ drawing

function drawMarks(g: CanvasRenderingContext2D, layout: FigureLayout, marks: readonly FigureMark[]): void {
  g.save()
  g.beginPath()
  g.rect(layout.map.x, layout.map.y, layout.map.w, layout.map.h)
  g.clip()
  for (const m of marks) {
    if (m.kind !== 'lines') continue
    g.strokeStyle = m.stroke
    g.lineWidth = m.width
    g.lineJoin = 'round'
    g.beginPath()
    for (const pts of m.lines) {
      pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)))
      if (m.closed) g.closePath()
    }
    g.stroke()
  }
  g.restore()
  for (const m of marks) {
    if (m.kind !== 'pin') continue
    g.beginPath()
    g.arc(m.x, m.y, m.r, 0, Math.PI * 2)
    g.fillStyle = m.fill
    g.fill()
    g.lineWidth = m.r * 0.35
    g.strokeStyle = '#ffffff'
    g.stroke()
    g.font = `bold ${m.px}px ${FIGURE_FONT}`
    const end = m.anchor === 'end'
    const lx = end ? m.x - m.r * 1.8 : m.x + m.r * 1.8
    g.textAlign = end ? 'right' : 'left'
    g.lineJoin = 'round'
    g.lineWidth = m.px * 0.22
    g.strokeText(m.label, lx, m.y + m.px * 0.35)
    g.fillStyle = '#111111'
    g.fillText(m.label, lx, m.y + m.px * 0.35)
  }
}

/** The figure on a canvas of its full size: the PNG. */
export function drawFigure(layout: FigureLayout, map: HTMLCanvasElement, marks: readonly FigureMark[]): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = layout.width
  canvas.height = layout.height
  const g = canvas.getContext('2d')
  if (!g) return canvas
  g.fillStyle = '#ffffff'
  g.fillRect(0, 0, layout.width, layout.height)
  g.imageSmoothingEnabled = true
  g.imageSmoothingQuality = 'high'
  g.drawImage(map, layout.map.x, layout.map.y, layout.map.w, layout.map.h)
  drawMarks(g, layout, marks)
  g.strokeStyle = '#333333'
  g.lineWidth = layout.hairline
  if (layout.bar) {
    const b = layout.bar
    const grad = g.createLinearGradient(b.x, 0, b.x + b.w, 0)
    for (const s of b.stops) grad.addColorStop(Math.min(1, Math.max(0, s.pos)), `rgb(${s.rgb[0]}, ${s.rgb[1]}, ${s.rgb[2]})`)
    g.fillStyle = grad
    g.fillRect(b.x, b.y, b.w, b.h)
    g.strokeRect(b.x + 0.5, b.y + 0.5, b.w - 1, b.h - 1)
  }
  for (const tk of layout.ticks) {
    g.beginPath()
    g.moveTo(tk.x, tk.y1)
    g.lineTo(tk.x, tk.y2)
    g.stroke()
  }
  for (const s of layout.swatches) {
    g.fillStyle = `rgb(${s.rgb[0]}, ${s.rgb[1]}, ${s.rgb[2]})`
    g.fillRect(s.x, s.y, s.w, s.h)
    g.strokeRect(s.x + 0.5, s.y + 0.5, s.w - 1, s.h - 1)
  }
  for (const tx of layout.texts) {
    g.font = `${tx.bold ? 'bold ' : ''}${tx.px}px ${FIGURE_FONT}`
    g.textAlign = tx.anchor === 'middle' ? 'center' : tx.anchor === 'end' ? 'right' : 'left'
    g.fillStyle = tx.role === 'caption' ? '#333333' : '#111111'
    g.fillText(tx.text, tx.x, tx.y)
  }
  return canvas
}

function download(blob: Blob, filename: string): void {
  if (typeof URL.createObjectURL !== 'function') return
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

async function copyText(text: string, fallbackFrom: HTMLTextAreaElement): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    fallbackFrom.focus()
    fallbackFrom.select()
    try {
      return document.execCommand('copy')
    } catch {
      return false
    }
  }
}

// ------------------------------------------------------------------ the dialog

export function isFigureDialogOpen(): boolean {
  return !!document.getElementById(DIALOG_ID)
}

export function closeFigureDialog(): void {
  document.getElementById(DIALOG_ID)?.remove()
}

interface Built {
  readonly layout: FigureLayout
  readonly map: HTMLCanvasElement
  readonly marks: readonly FigureMark[]
  readonly png: HTMLCanvasElement
  readonly source: { width: number; height: number } | null
  readonly kind: FigureKind
  readonly size: FigureSize
  readonly pins: number
}

type Raster = { canvas: HTMLCanvasElement; source: { width: number; height: number } | null; project: GlobeCapture['project'] | null }

/** What one figure is of: read once from the source, so every part of it describes one frame. */
interface FigureContext {
  readonly citation: CitationInput | null
  readonly frame: FigureFrame | null
  readonly change: FigureChange | null
  readonly datasetTitle: string
  readonly units: string | null
  readonly frameMs: number | null
  readonly caption: string
}

function figureContext(src: FigureSource): FigureContext {
  const citation = src.citation()
  return {
    citation,
    frame: src.frame(),
    change: src.change(),
    datasetTitle: src.datasetTitle() ?? citation?.title ?? '',
    units: src.units(),
    frameMs: citation?.frameMs ?? null,
    caption: citation ? citationText(citation) : '',
  }
}

/** The title a figure starts with: the dataset and its frame, as precise as the data. */
function defaultFigureTitle(ctx: FigureContext): string {
  return ctx.frameMs !== null ? `${ctx.datasetTitle}, ${formatFrameTime(ctx.frameMs, ctx.citation?.framePrecision)}` : ctx.datasetTitle
}

/**
 * AYNI (S8): the settings the dialog last showed, so the reproducibility
 * bundle's figure is the one the user set up; null until the dialog has
 * been opened in this session (the bundle then uses the defaults).
 */
export interface FigureSettings {
  readonly kind: FigureKind
  readonly size: FigureSize
  /** null: the default title (the dataset and its frame). */
  readonly title: string | null
  /** null: pins when there are any. */
  readonly withPins: boolean | null
}
let lastSettings: FigureSettings | null = null

export function figureSettings(): FigureSettings | null {
  return lastSettings
}

/** The map for a kind and layout: the Equal Earth raster, or the globe as rendered. */
async function figureRaster(src: FigureSource, ctx: FigureContext, kind: FigureKind, layout: FigureLayout): Promise<Raster | null> {
  if (kind === 'equal-earth') {
    if (!ctx.frame) return null
    return { canvas: paintEqualEarth(ctx.frame, ctx.change, layout.map.w, layout.map.h), source: null, project: null }
  }
  const cap = await src.captureGlobe(layout.map.w, layout.map.h)
  return cap ? { canvas: cap.canvas, source: cap.source, project: (lat, lon) => cap.project(lat, lon) } : null
}

/** The vector marks over the map: the Equal Earth frame and coasts, and the pins with their names. */
async function figureMarks(src: FigureSource, kind: FigureKind, withPins: boolean, layout: FigureLayout, project: GlobeCapture['project'] | null): Promise<FigureMark[]> {
  const marks: FigureMark[] = []
  const mm = (v: number) => (v / 25.4) * FIGURE_DPI
  let place: ((lat: number, lon: number) => [number, number] | null) | null = null
  if (kind === 'equal-earth') {
    marks.push(...equalEarthFrame(layout.map, layout.hairline))
    const land = await loadCoasts()
    const coast = land ? equalEarthCoasts(land, layout.map, layout.hairline) : null
    if (coast) marks.push(coast)
    const proj = equalEarthProjector(layout.map)
    place = (lat, lon) => proj(lon, lat)
  } else if (project) {
    place = (lat, lon) => {
      const p = project(lat, lon)
      return p ? [layout.map.x + p.x * layout.map.w, layout.map.y + p.y * layout.map.h] : null
    }
  }
  if (withPins && place) {
    for (const pin of src.pins()) {
      const at = place(pin.lat, pin.lon)
      if (!at) continue
      const r = mm(0.9)
      const px = (6 * FIGURE_DPI) / 72
      // Near the right edge the name goes to the left of the dot, so it is never cut off.
      const anchor = at[0] + r * 1.8 + measure(pin.label, px, true) > layout.width - mm(1) ? 'end' : 'start'
      marks.push({ kind: 'pin', x: at[0], y: at[1], r, fill: pin.color, label: pin.label, px, anchor })
    }
  }
  return marks
}

/** The page: title, subtitle, map box, legend and caption, for this kind and size. */
function figureLayoutOf(ctx: FigureContext, kind: FigureKind, size: FigureSize, title: string): FigureLayout {
  const legend = figureLegend(ctx.frame, ctx.change, ctx.units, ctx.datasetTitle, kind)
  const subtitle = kind === 'globe' ? 'Globe, as shown on screen' : 'Equal Earth projection, whole frame' // i18n-exempt: figure text, English like the citation
  return layoutFigure({ size, dpi: FIGURE_DPI, title, subtitle, mapAspect: kind === 'globe' ? (size === 'single' ? 1 : 1.5) : EQUAL_EARTH_ASPECT, legend, caption: ctx.caption }, measure)
}

/** A finished figure from a map: the marks over it and the page drawn at 300 dpi. */
async function composeFigure(src: FigureSource, kind: FigureKind, size: FigureSize, withPins: boolean, layout: FigureLayout, map: Raster): Promise<Built> {
  const marks = await figureMarks(src, kind, withPins, layout, map.project)
  const png = drawFigure(layout, map.canvas, marks)
  return { layout, map: map.canvas, marks, png, source: map.source, kind, size, pins: marks.filter((m) => m.kind === 'pin').length }
}

/** How the map was made, for the files' metadata and the methods paragraph. */
function figureSourceNote(ctx: FigureContext, b: Built): string {
  const grid = ctx.frame ? `; data grid ${ctx.frame.snapshot.width} × ${ctx.frame.snapshot.height} cells` : ''
  const from = b.source ? `, from a render of ${b.source.width} × ${b.source.height} px${isEnlarged(b.source, b.layout.map) ? ' (enlarged)' : ''}` : ', each pixel the nearest grid cell'
  const what = b.kind === 'globe' ? 'the globe as rendered on screen' : 'the whole frame on the Equal Earth projection (Šavrič, Patterson & Jenny 2019)'
  return `Map: ${what}, a raster of ${b.layout.map.w} × ${b.layout.map.h} px${from}${grid}. Title, colour bar, ticks, keys, pins and caption are vectors in the SVG version.` // i18n-exempt: file metadata, English like the citation
}

/** The PNG file: 300 dpi and its title, caption, source and software inside it. */
async function figurePngBlob(ctx: FigureContext, b: Built, title: string): Promise<Blob | null> {
  const blob = await new Promise<Blob | null>((resolve) => b.png.toBlob(resolve, 'image/png'))
  if (!blob) return null
  const bytes = withPngMetadata(new Uint8Array(await blob.arrayBuffer()), FIGURE_DPI, {
    Title: title,
    Description: ctx.caption,
    Source: figureSourceNote(ctx, b),
    Software: ctx.citation ? `${ctx.citation.software}${ctx.citation.softwareDoi ? ` (https://doi.org/${ctx.citation.softwareDoi})` : ''}` : 'AYNI XR',
    'Creation Time': new Date().toISOString(),
  })
  return new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'image/png' })
}

/** The SVG file: text, colour bar, keys and pins as vectors over the map. */
function figureSvgText(ctx: FigureContext, b: Built, title: string): string {
  return figureSvg(b.layout, {
    mapHref: b.map.toDataURL('image/png'),
    marks: b.marks,
    title,
    description: `${ctx.caption} ${figureSourceNote(ctx, b)}`,
  })
}

/** What the methods paragraph says about an exported figure. */
function methodsFigureOf(ctx: FigureContext, b: Built): MethodsFigure {
  const display = ctx.frame && !ctx.change && !isDefaultDisplay(ctx.frame.display) ? displayOf(ctx.frame) : null
  return {
    kind: b.kind,
    widthMm: b.layout.widthMm,
    dpi: FIGURE_DPI,
    widthPx: b.layout.width,
    heightPx: b.layout.height,
    mapPx: { width: b.layout.map.w, height: b.layout.map.h },
    sourcePx: b.source,
    pins: b.pins,
    display,
  }
}

/** The figure's record entry (research Khipu, slice 4): its settings, what the colour bar shows, the file. */
function trailFigureFile(ctx: FigureContext, b: Built, title: string, format: 'png' | 'svg', name: string, body: Blob | string): void {
  trailFile({ name, body, kind: format })
  const legend = figureLegend(ctx.frame, ctx.change, ctx.units, ctx.datasetTitle ?? '', b.kind)
  const change = ctx.change
  trail({
    type: 'figure',
    frame: true,
    data: {
      file: name,
      format,
      map: b.kind,
      widthMm: b.layout.widthMm,
      dpi: FIGURE_DPI,
      widthPx: b.layout.width,
      heightPx: b.layout.height,
      mapPx: { width: b.layout.map.w, height: b.layout.map.h },
      ...(b.source ? { renderPx: { width: b.source.width, height: b.source.height } } : {}),
      title,
      pins: b.pins,
      ...(legend ? { legend: legend.title } : {}),
      ...(change ? { change: change.kind, ...('mode' in change && change.mode ? { mode: change.mode } : {}), limit: change.limit, ...('min' in change && change.min !== undefined ? { min: change.min } : {}) } : {}),
      source: figureSourceNote(ctx, b),
      summary: `Figure (${format.toUpperCase()}, ${b.kind === 'globe' ? 'the globe' : 'Equal Earth'}, ${b.layout.widthMm} mm at ${FIGURE_DPI} dpi): ${title}`, // i18n-exempt: record text, English like the CSV
    },
  })
}

/** One figure's files, for the reproducibility bundle. */
export interface FigureFiles {
  readonly png: Blob
  readonly svg: string
  readonly figure: MethodsFigure
  readonly settings: { readonly kind: FigureKind; readonly size: FigureSize; readonly title: string; readonly pins: number }
}

/**
 * AYNI (S8): the figure as the dialog would export it now, with the
 * dialog's last settings (or its defaults: the globe, one column, the
 * default title, the pins when there are any), without opening it; null
 * when there is nothing to draw (no globe, no frame for the flat map).
 * The Equal Earth map falls back to the globe when there is no frame.
 */
export async function renderFigureFiles(src: FigureSource): Promise<FigureFiles | null> {
  const ctx = figureContext(src)
  const s = lastSettings
  const kind: FigureKind = s?.kind === 'equal-earth' && ctx.frame ? 'equal-earth' : 'globe'
  const size: FigureSize = s?.size ?? 'single'
  const title = s?.title ?? defaultFigureTitle(ctx)
  const withPins = (s?.withPins ?? true) && src.pins().length > 0
  const layout = figureLayoutOf(ctx, kind, size, title)
  let map: Raster | null = null
  try {
    map = await figureRaster(src, ctx, kind, layout)
  } catch {
    map = null
  }
  if (!map) return null
  const b = await composeFigure(src, kind, size, withPins, layout, map)
  const png = await figurePngBlob(ctx, b, title)
  if (!png) return null
  return { png, svg: figureSvgText(ctx, b, title), figure: methodsFigureOf(ctx, b), settings: { kind, size, title, pins: b.pins } }
}

/** Open the dialog on one tab; replaces any dialog already open. */
export function openFigureDialog(
  src: FigureSource,
  /** AYNI: `kind` starts the figure on the globe or the Equal Earth map (the chatbot's `figure.open`); the globe by default. */
  opts: { tab?: FigureTab; trigger?: HTMLElement | null; announce?: (message: string) => void; kind?: FigureKind } = {},
): void {
  closeFigureDialog()
  const paused = src.pause?.() ?? false
  const ctx = figureContext(src)
  const { citation, frame, datasetTitle, frameMs } = ctx
  let tab: FigureTab = opts.tab ?? 'figure'
  // A flat map needs the frame's values; without them the globe it is.
  let kind: FigureKind = opts.kind === 'equal-earth' && frame ? 'equal-earth' : 'globe'
  let size: FigureSize = 'single'
  const startTitle = defaultFigureTitle(ctx)
  let title = startTitle
  let withPins = src.pins().length > 0
  let exported: MethodsFigure | null = null
  let built: Built | null = null
  let drawing = 0
  const rasters = new Map<string, Raster>()
  // AYNI (S8): the reproducibility bundle draws the figure with these settings.
  const remember = () => {
    lastSettings = { kind, size, title: title === startTitle ? null : title, withPins: src.pins().length ? withPins : null }
  }
  remember()

  const px = (mm: number) => formatNumber(mmToPx(mm))
  const backdrop = document.createElement('div')
  backdrop.id = DIALOG_ID
  backdrop.className = 'cite-backdrop figure-backdrop'
  backdrop.innerHTML = `
    <div class="cite-dialog figure-dialog" role="dialog" aria-modal="true" aria-labelledby="figure-title-h">
      <h2 class="cite-title" id="figure-title-h">${escapeHtml(t('research.figure.title'))}</h2>
      <p class="cite-dataset">${escapeHtml(datasetTitle)}</p>
      ${frameMs !== null ? `<p class="cite-sub">${escapeHtml(t('cite.frame', { time: formatFrameTime(frameMs, citation?.framePrecision) }))}</p>` : ''}
      ${paused ? `<p class="cite-sub figure-paused">${escapeHtml(t('research.figure.paused'))}</p>` : ''}
      <div class="cite-tabs" role="tablist" aria-label="${escapeAttr(t('research.figure.tabs'))}">
        <button type="button" role="tab" class="cite-tab" id="figure-tab-figure" data-tab="figure" aria-controls="figure-panel-figure">${escapeHtml(t('research.figure.tab.figure'))}</button>
        <button type="button" role="tab" class="cite-tab" id="figure-tab-methods" data-tab="methods" aria-controls="figure-panel-methods">${escapeHtml(t('research.figure.tab.methods'))}</button>
      </div>
      <div id="figure-panel-figure" role="tabpanel" aria-labelledby="figure-tab-figure" class="figure-panel">
        <fieldset class="figure-field">
          <legend>${escapeHtml(t('research.figure.map'))}</legend>
          <label class="figure-choice"><input type="radio" name="figure-kind" value="globe"> ${escapeHtml(t('research.figure.globe'))}</label>
          <label class="figure-choice"><input type="radio" name="figure-kind" value="equal-earth"${frame ? '' : ' disabled'}> ${escapeHtml(t('research.figure.flat'))}</label>
        </fieldset>
        <fieldset class="figure-field">
          <legend>${escapeHtml(t('research.figure.size'))}</legend>
          <label class="figure-choice"><input type="radio" name="figure-size" value="single"> ${escapeHtml(t('research.figure.single', { mm: FIGURE_WIDTH_MM.single, px: px(FIGURE_WIDTH_MM.single) }))}</label>
          <label class="figure-choice"><input type="radio" name="figure-size" value="double"> ${escapeHtml(t('research.figure.double', { mm: FIGURE_WIDTH_MM.double, px: px(FIGURE_WIDTH_MM.double) }))}</label>
        </fieldset>
        <label class="cite-link-label" for="figure-title-input">${escapeHtml(t('research.figure.titleLabel'))}</label>
        <input id="figure-title-input" class="cite-link figure-title-input" type="text" value="${escapeAttr(title)}" spellcheck="true">
        <label class="figure-choice figure-pins"><input type="checkbox" id="figure-pins"${withPins ? ' checked' : ' disabled'}> ${escapeHtml(t(src.pins().length ? 'research.figure.pins' : 'research.figure.noPins'))}</label>
        <div class="figure-preview" aria-busy="true"><p class="figure-wait">${escapeHtml(t('research.figure.drawing'))}</p></div>
        <p class="figure-meta" aria-live="polite"></p>
        <div class="cite-actions">
          <span class="cite-status figure-status" role="status" aria-live="polite"></span>
          <button type="button" class="figure-svg" disabled>${escapeHtml(t('research.figure.svg'))}</button>
          <button type="button" class="cite-copy figure-png" disabled>${escapeHtml(t('research.figure.png'))}</button>
        </div>
        <p class="cite-note">${escapeHtml(t('research.figure.note'))}</p>
      </div>
      <div id="figure-panel-methods" role="tabpanel" aria-labelledby="figure-tab-methods" class="figure-panel" hidden>
        <label class="cite-link-label" for="figure-methods-text">${escapeHtml(t('research.methods.label'))}</label>
        <textarea id="figure-methods-text" class="cite-output figure-methods-text" readonly rows="12" spellcheck="false"></textarea>
        <div class="cite-actions">
          <span class="cite-status methods-status" role="status" aria-live="polite"></span>
          <button type="button" class="methods-download">${escapeHtml(t('research.methods.download'))}</button>
          <button type="button" class="cite-copy methods-copy">${escapeHtml(t('research.methods.copy'))}</button>
        </div>
        <p class="cite-note">${escapeHtml(t('research.methods.note'))}</p>
      </div>
      <div class="cite-footer">
        <button type="button" class="cite-close figure-close">${escapeHtml(t('cite.close'))}</button>
      </div>
    </div>`
  document.body.appendChild(backdrop)

  const dialog = backdrop.querySelector<HTMLElement>('.figure-dialog')!
  const q = <T extends HTMLElement>(sel: string) => backdrop.querySelector<T>(sel)!
  const preview = q<HTMLElement>('.figure-preview')
  const meta = q<HTMLElement>('.figure-meta')
  const status = q<HTMLElement>('.figure-status')
  const pngBtn = q<HTMLButtonElement>('.figure-png')
  const svgBtn = q<HTMLButtonElement>('.figure-svg')
  const titleInput = q<HTMLInputElement>('#figure-title-input')
  const pinsBox = q<HTMLInputElement>('#figure-pins')
  const methodsArea = q<HTMLTextAreaElement>('#figure-methods-text')
  const methodsStatus = q<HTMLElement>('.methods-status')
  const tabs = [...backdrop.querySelectorAll<HTMLButtonElement>('.cite-tab')]
  ;(backdrop.querySelector<HTMLInputElement>(`input[name="figure-kind"][value="${kind}"]`)!).checked = true
  ;(backdrop.querySelector<HTMLInputElement>(`input[name="figure-size"][value="${size}"]`)!).checked = true

  const say = (el: HTMLElement, message: string) => {
    el.textContent = message
    opts.announce?.(message)
  }

  const methodsNow = (): string => {
    const input = src.methods(exported)
    return input ? methodsText(input) : t('research.methods.none')
  }
  const showTab = (next: FigureTab, focus = false) => {
    tab = next
    for (const b of tabs) {
      const on = b.dataset.tab === tab
      b.setAttribute('aria-selected', String(on))
      b.tabIndex = on ? 0 : -1
      if (on && focus) b.focus()
    }
    q<HTMLElement>('#figure-panel-figure').hidden = tab !== 'figure'
    q<HTMLElement>('#figure-panel-methods').hidden = tab !== 'methods'
    if (tab === 'methods') methodsArea.value = methodsNow()
  }

  /** The map for this kind and size, drawn once and kept while the dialog is open. */
  const raster = async (layout: FigureLayout): Promise<Raster | null> => {
    const key = `${kind}|${size}|${layout.map.w}x${layout.map.h}`
    const hit = rasters.get(key)
    if (hit) return hit
    const made = await figureRaster(src, ctx, kind, layout)
    if (made) rasters.set(key, made)
    return made
  }

  const draw = async (): Promise<void> => {
    const mine = ++drawing
    pngBtn.disabled = true
    svgBtn.disabled = true
    preview.setAttribute('aria-busy', 'true')
    remember()
    const layout = figureLayoutOf(ctx, kind, size, title)
    let map: Awaited<ReturnType<typeof raster>> = null
    try {
      map = await raster(layout)
    } catch {
      map = null
    }
    if (mine !== drawing || !backdrop.isConnected) return
    if (!map) {
      built = null
      preview.innerHTML = `<p class="figure-wait">${escapeHtml(t(kind === 'globe' ? 'research.figure.noGlobe' : 'research.figure.noFrame'))}</p>`
      preview.setAttribute('aria-busy', 'false')
      meta.textContent = ''
      return
    }
    const next = await composeFigure(src, kind, size, withPins, layout, map)
    if (mine !== drawing || !backdrop.isConnected) return
    const png = next.png
    png.className = 'figure-canvas'
    png.setAttribute('role', 'img')
    png.setAttribute('aria-label', t('research.figure.previewAria', { title: title || datasetTitle }))
    preview.replaceChildren(png)
    preview.setAttribute('aria-busy', 'false')
    built = next
    const grid = frame ? t('research.figure.metaGrid', { w: formatNumber(frame.snapshot.width), h: formatNumber(frame.snapshot.height) }) : ''
    const enlarged = map.source && isEnlarged(map.source, layout.map)
      ? t('research.figure.metaEnlarged', { w: formatNumber(map.source.width), h: formatNumber(map.source.height) })
      : ''
    meta.textContent = [
      t('research.figure.meta', { w: formatNumber(layout.width), h: formatNumber(layout.height), dpi: FIGURE_DPI, mm: layout.widthMm, mw: formatNumber(layout.map.w), mh: formatNumber(layout.map.h) }),
      enlarged,
      grid,
    ].filter(Boolean).join(' ')
    pngBtn.disabled = false
    svgBtn.disabled = false
  }

  const figureDone = (b: Built) => {
    exported = methodsFigureOf(ctx, b)
    if (tab === 'methods') methodsArea.value = methodsNow()
  }
  const filename = (ext: string) => ayniCsvFilename('figure', datasetTitle, new Date()).replace(/\.csv$/, `.${ext}`)

  pngBtn.addEventListener('click', async () => {
    const b = built
    if (!b) return
    const file = await figurePngBlob(ctx, b, title)
    if (!file) return
    const name = filename('png')
    download(file, name)
    trailFigureFile(ctx, b, title, 'png', name, file)
    figureDone(b)
    say(status, t('research.figure.saved', { file: name }))
  })
  svgBtn.addEventListener('click', () => {
    const b = built
    if (!b) return
    const svg = figureSvgText(ctx, b, title)
    const name = filename('svg')
    download(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }), name)
    trailFigureFile(ctx, b, title, 'svg', name, svg)
    figureDone(b)
    say(status, t('research.figure.saved', { file: name }))
  })

  for (const input of backdrop.querySelectorAll<HTMLInputElement>('input[name="figure-kind"], input[name="figure-size"]')) {
    input.addEventListener('change', () => {
      if (!input.checked) return
      if (input.name === 'figure-kind') kind = input.value as FigureKind
      else size = input.value as FigureSize
      void draw()
    })
  }
  let titleTimer = 0
  titleInput.addEventListener('input', () => {
    window.clearTimeout(titleTimer)
    titleTimer = window.setTimeout(() => {
      title = titleInput.value
      void draw()
    }, 350)
  })
  pinsBox.addEventListener('change', () => {
    withPins = pinsBox.checked
    void draw()
  })

  for (const b of tabs) b.addEventListener('click', () => showTab(b.dataset.tab as FigureTab))
  q<HTMLElement>('.cite-tabs').addEventListener('keydown', (e) => {
    const key = (e as KeyboardEvent).key
    if (key !== 'ArrowRight' && key !== 'ArrowLeft') return
    e.preventDefault()
    showTab(tab === 'figure' ? 'methods' : 'figure', true)
  })
  /** AYNI: a research record keeps the paragraph as it was copied or saved (slice 4), with the methods it cites. */
  const trailMethods = (action: 'copied' | 'downloaded', text: string) => {
    const input = src.methods(exported)
    const cited = input ? buildMethods(input).cited : []
    trail({
      type: 'methods',
      data: { action, text, cited: [...cited], summary: `Methods paragraph ${action} (${cited.length} methods cited)` }, // i18n-exempt: record text
    })
  }
  q<HTMLButtonElement>('.methods-copy').addEventListener('click', async () => {
    const text = methodsNow()
    const ok = await copyText(text, methodsArea)
    if (ok) trailMethods('copied', text)
    say(methodsStatus, t(ok ? 'research.methods.copied' : 'cite.copyFailed'))
  })
  q<HTMLButtonElement>('.methods-download').addEventListener('click', () => {
    const name = ayniCsvFilename('methods', datasetTitle, new Date()).replace(/\.csv$/, '.txt')
    const text = methodsNow()
    download(new Blob([text], { type: 'text/plain;charset=utf-8' }), name)
    trailFile({ name, body: text, kind: 'txt' })
    trailMethods('downloaded', text)
    say(methodsStatus, t('research.figure.saved', { file: name }))
  })

  const finish = () => {
    window.removeEventListener('keydown', onKey, true)
    drawing++
    backdrop.remove()
    // The panel under the dialog may have been redrawn (playback paused): its new button then.
    const trigger = opts.trigger
    const find = (): HTMLElement | null => (trigger?.isConnected ? trigger : trigger?.classList.length ? document.querySelector<HTMLElement>(`.${[...trigger.classList].join('.')}`) : null)
    find()?.focus()
    // A redraw that lands just after (playback settling) replaces the button: follow it once.
    if (trigger) {
      window.setTimeout(() => {
        const active = document.activeElement
        if (!active || active === document.body || !active.isConnected) find()?.focus()
      }, 600)
    }
  }
  q<HTMLButtonElement>('.figure-close').addEventListener('click', finish)
  backdrop.addEventListener('pointerdown', (e) => {
    if (e.target === backdrop) finish()
  })
  // Modal: Escape and Tab are the dialog's before any panel's (as in "Cite this view").
  function onKey(e: KeyboardEvent): void {
    if (!backdrop.isConnected) {
      window.removeEventListener('keydown', onKey, true)
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      finish()
    } else if (e.key === 'Tab') {
      const stops = [...dialog.querySelectorAll<HTMLElement>('[role="tab"][aria-selected="true"], input, textarea, button:not([role="tab"])')]
        .filter((el) => !(el as HTMLButtonElement).disabled && el.getClientRects().length > 0 && !(el instanceof HTMLInputElement && el.type === 'radio' && !el.checked))
      const i = stops.indexOf(document.activeElement as HTMLElement)
      const next = e.shiftKey ? (i <= 0 ? stops.length - 1 : i - 1) : (i + 1) % stops.length
      e.preventDefault()
      stops[next]?.focus()
    }
  }
  window.addEventListener('keydown', onKey, true)

  showTab(tab)
  tabs[tab === 'figure' ? 0 : 1].focus()
  void draw()
}

/** A render smaller than the map by more than rounding (1 %): the map was enlarged from it. */
function isEnlarged(source: { width: number; height: number }, map: { w: number; h: number }): boolean {
  return source.width < map.w * 0.99 || source.height < map.h * 0.99
}

/** The Tools → View colours in values, for the methods paragraph. */
function displayOf(frame: FigureFrame): MethodsFigure['display'] {
  const d = frame.display
  const stretched = d.stretch.lo !== 0 || d.stretch.hi !== 1
  const a = valueAtPosition(frame.scale, d, 0)
  const b = valueAtPosition(frame.scale, d, 1)
  return {
    palette: d.palette === 'source' ? "dataset's own" : d.palette, // i18n-exempt: methods text, English like the citation
    inverted: !!d.inverted,
    range: stretched && Number.isFinite(a) && Number.isFinite(b) ? { min: Math.min(a, b), max: Math.max(a, b) } : null,
    threshold: d.threshold.min !== null || d.threshold.max !== null ? { ...d.threshold } : null,
  }
}
